import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ApolloError } from '@apollo/client'

import { ModifierGroup } from 'src/apollo/onlineOrdering'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { Channel } from 'public/js/siteUtilities'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import NotFound404 from 'shared/components/errors/NotFound404'

import { EcommStickyFooter } from 'public/components/default_template/EcommStickyFooter'
import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import PageMeta from 'public/components/default_template/meta/PageMeta'
import { ItemResolutionModal } from 'public/components/default_template/online_ordering/item_modal/ItemResolutionModal'
import OrderPageWrapper, {
  OrderPageContentProps
} from 'public/components/default_template/online_ordering/pages/OrderPageWrapper'
import { ModifierContextProvider } from 'public/components/online_ordering/ModifierContext'
import { Action } from 'public/components/online_ordering/types'

import { useDisplayPageContext } from '../../DisplayPageContext'
import {
  useDoMenuItemWithFallback,
  useMenuItemDetailsWithFallback
} from '../hooks/menuItemQueries'
import { VomProductDisplay } from 'public/components/default_template/product_display/view_only_menu_product_display/VomProductDisplay'
import { collapseModifierGroups } from 'public/components/online_ordering/modifierUtils'

type LocationState = {
  menuItem?: MenuItem
}

export const VomProductDisplayPageContent = ({
  itemGuid,
  itemGroupGuid
}: OrderPageContentProps) => {
  const { track } = useThrottledTracker()
  const productDisplayPageViewRef = useRef(false)
  const { restaurant: siteRestaurant } = useRestaurant()
  const { channelGuid } = useDisplayPageContext()
  const { state } = useLocation<LocationState>()
  const [showFooter, setShowFooter] = useState(false)

  const {
    data,
    error,
    loading: menuItemDetailsLoading
  } = useMenuItemDetailsWithFallback(itemGuid, itemGroupGuid)

  // massage menuItemDetails into the MenuItem type
  const menuItem: MenuItem | undefined | null = useMemo(
    () =>
      data?.menuItemDetails
        ? {
            ...data?.menuItemDetails,
            guid: itemGuid || state?.menuItem?.guid || null,
            itemGroupGuid: itemGroupGuid || ''
          }
        : state?.menuItem,
    [data?.menuItemDetails, state?.menuItem, itemGroupGuid, itemGuid]
  )

  const {
    data: doMenuItemData,
    loading: doMenuItemLoading,
    isVomOnly
  } = useDoMenuItemWithFallback(itemGuid, itemGroupGuid)

  const rootRef = useRef<HTMLDivElement>(null)
  const modifierGroups = useMemo(
    () =>
      data?.menuItemDetails.modifierGroups
        ? collapseModifierGroups(data?.menuItemDetails.modifierGroups)
        : {},
    [data]
  )
  const rootModifier = useMemo(
    () => ({
      groupGuid: itemGroupGuid || 'root',
      modifier: {
        itemGuid: itemGuid!,
        itemGroupGuid,
        modifierGroups: data?.menuItemDetails.modifierGroups || [],
        price: data?.menuItemDetails.prices[0] || 0
      },
      modifierGroups,
      isRoot: true,
      masterId: data?.menuItemDetails?.masterId,
      action: 'replace' as Action,
      price: data?.menuItemDetails.prices[0] || 0,
      usesFractionalQuantity: data?.menuItemDetails?.usesFractionalQuantity,
      unitOfMeasure: data?.menuItemDetails?.unitOfMeasure,
      name: data?.menuItemDetails.name
    }),
    [itemGuid, itemGroupGuid, data?.menuItemDetails, modifierGroups]
  )

  const doMenuItem =
    !doMenuItemLoading &&
    doMenuItemData?.doMenus_findMenuItem?.__typename ===
      'DoMenus_FindMenuItemResponse'
      ? doMenuItemData?.doMenus_findMenuItem
      : undefined
  const isAvailableNow = !!doMenuItem?.menuItem.isAvailableNow && !isVomOnly

  const modifierMap = useMemo(() => {
    if (isAvailableNow) {
      return doMenuItem?.menuResponse?.modifierGroupReferences?.reduce(
        (map, ref) => ({
          ...map,
          [ref.guid]: ref
        }),
        {}
      )
    } else {
      return {}
    }
  }, [isAvailableNow, doMenuItem])

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      menuItem &&
      !productDisplayPageViewRef.current
    ) {
      track(TrackingEvents.PRODUCT_DISPLAY_PAGE_VIEW, {
        name: menuItem?.name,
        guid: menuItem?.guid,
        outOfStock: !!menuItem.outOfStock,
        hasImage: Boolean(menuItem.image)
      })
      productDisplayPageViewRef.current = true
    }
    // run once when all the data has finished loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, menuItem])

  const [pageMetaTitle, pageMetaDescription] = useMemo(() => {
    const restaurantName = siteRestaurant?.name || ''

    const itemName = menuItem?.name || undefined
    const defaultTitle = 'Product Display Page'
    const formattedTitleRestaurantDetails = restaurantName
      ? ` | ${restaurantName}`
      : ''
    const titleMeta = `${
      itemName || defaultTitle
    }${formattedTitleRestaurantDetails}`

    const itemDescr = menuItem?.description || ''
    const itemDescrEndsWithPunctuation =
      itemDescr.endsWith('.') || itemDescr.endsWith('!')
    const formattedItemDescr = itemDescr ? `: ${itemDescr}` : ''
    const descrPunctuation = itemDescrEndsWithPunctuation ? '' : '!'
    const formattedDescrRestaurantDetails = restaurantName
      ? ` Try it today at ${restaurantName}!`
      : ''
    const descrMeta = itemName
      ? `Enjoy our ${itemName}${formattedItemDescr}${descrPunctuation}${formattedDescrRestaurantDetails}`
      : undefined

    return [titleMeta, descrMeta]
  }, [menuItem, siteRestaurant?.name])

  return (
    <>
      <PageMeta title={pageMetaTitle} description={pageMetaDescription} />
      <div className='pageContent'>
        <div className='paddedContentWrapper productDisplayWrapper'>
          {error && itemNotFound(error) && (
            <NotFound404
              meta={siteRestaurant?.meta}
              siteTheme={siteRestaurant?.theme}
            />
          )}
          <ModifierContextProvider
            modifier={rootModifier}
            rootRef={rootRef}
            modifierGroupData={modifierMap}
            quantity={1}
          >
            <VomProductDisplay
              loading={menuItemDetailsLoading}
              menuItem={menuItem}
              modifierGroups={
                (data?.menuItemDetails?.modifierGroups as ModifierGroup[]) || []
              }
              setShowFooter={setShowFooter}
              isOrderable={isAvailableNow && !siteRestaurant?.config.isMenuOnly}
            />
            <ItemResolutionModal channelGuid={channelGuid} />
            <EcommStickyFooter showFooter={showFooter} />
          </ModifierContextProvider>
        </div>
      </div>
    </>
  )
}

const itemNotFound = (error: ApolloError | undefined) =>
  error?.graphQLErrors?.some(
    (err: any) => err?.extensions?.code === 'MENU_ITEM_NOT_FOUND'
  )

const ViewOnlyMenuProductDisplayPage = () => {
  return (
    <OrderPageWrapper channel={Channel.OO}>
      {(props) => <VomProductDisplayPageContent {...props} />}
    </OrderPageWrapper>
  )
}
export default React.memo(ViewOnlyMenuProductDisplayPage)
